import { useCallback, useEffect, useMemo, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { BASE_URL } from "../../config";
import { showConfirm, showError, showSuccess } from "../../utils/dialog";
import ActionDropdown from "../ActionDropdown";

function authHeaders(token) {
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function accountQuery(accountId) {
  return accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
}

export default function TwoStepVerificationSection({
  accountId: accountIdProp,
  compact = false,
  userDisplayName = "",
}) {
  const [securityData, setSecurityData] = useState({
    two_factor_enabled: false,
    force_2fa: false,
    verification_methods: [],
  });
  const [loading, setLoading] = useState(false);

  const [twoStepOpen, setTwoStepOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [emailSetupOpen, setEmailSetupOpen] = useState(false);
  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [emailListOpen, setEmailListOpen] = useState(false);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const [emailAddress, setEmailAddress] = useState("");
  const [emailMakeDefault, setEmailMakeDefault] = useState(true);
  const [emailOtp, setEmailOtp] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const resolvedAccountId =
    accountIdProp || localStorage.getItem("accountId") || "";

  const emailMethods = useMemo(
    () =>
      (securityData.verification_methods || []).filter(
        (m) => String(m.type).toLowerCase() === "email",
      ),
    [securityData.verification_methods],
  );

  const hasEmailMethod = emailMethods.length > 0;
  const isEnabled = !!securityData.two_factor_enabled;
  const forceOn = !!securityData.force_2fa;

  const loadSecurityData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const url = `${BASE_URL}/api/my-account/account/security${accountQuery(resolvedAccountId)}`;
      const res = await fetch(url, { headers: authHeaders(token) });
      const j = await res.json();
      if ((res.ok || j?.success) && j?.data) {
        setSecurityData(j.data);
      }
    } catch (e) {
      console.error("2FA security load error", e);
    }
  }, [resolvedAccountId]);

  useEffect(() => {
    loadSecurityData();
  }, [loadSecurityData]);

  const handleToggle2FA = async (enabled) => {
    const actionLabel = enabled ? "enable" : "disable";
    const confirmed = await showConfirm(
      `Are you sure you want to ${actionLabel} Force 2FA?`,
      "Confirm Force 2FA",
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/my-account/account/twofactor`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({ enabled, accountId: resolvedAccountId }),
      });
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || "2FA toggle failed");
      setSecurityData((prev) => ({
        ...prev,
        force_2fa: enabled,
        two_factor_enabled: enabled && hasEmailMethod,
      }));
      await loadSecurityData();
      showSuccess(
        `Force 2FA ${enabled ? "enabled" : "disabled"}`,
      );
    } catch (e) {
      showError(e.message || "Failed to update two-factor authentication");
    } finally {
      setLoading(false);
    }
  };

  const verifyPasswordAndContinue = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/my-account/accounts/verify-password`,
        {
          method: "POST",
          headers: authHeaders(localStorage.getItem("token")),
          body: JSON.stringify({ password }),
        },
      );
      const data = await res.json();
      if (!data.success) {
        setPasswordError("Invalid password");
        return;
      }
      setVerifyOpen(false);
      setPassword("");
      setPasswordError("");

      if (pendingAction === "addEmail") {
        setEmailSetupOpen(true);
      } else if (pendingAction === "manageEmail") {
        setEmailListOpen(true);
      }
      setPendingAction(null);
    } catch {
      setPasswordError("Invalid password");
    }
  };

  const openPasswordGate = (action) => {
    setPendingAction(action);
    setTwoStepOpen(false);
    setVerifyOpen(true);
  };

  const sendEmailOtp = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/verifications/methods${accountQuery(resolvedAccountId)}`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({
            email: emailAddress,
            makeDefault: emailMakeDefault,
            accountId: resolvedAccountId,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to send code");
      }
      setPendingToken(data.pendingToken || data.data?.pendingToken || "");
      setEmailSetupOpen(false);
      setEmailOtpOpen(true);
      showSuccess("Verification code sent to your email");
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOtp = async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/verifications/methods/verify`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({
            pendingToken,
            code: emailOtp,
            accountId: resolvedAccountId,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Invalid code");
      }
      setEmailOtpOpen(false);
      setEmailOtp("");
      setEmailAddress("");
      setPendingToken("");
      showSuccess("Email 2-step verification enabled successfully");
      await loadSecurityData();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendEmailOtp = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/verifications/methods/resend`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({ pendingToken }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Resend failed");
      }
      showSuccess("Verification code resent");
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const setDefaultEmail = async (methodId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/verifications/methods/${methodId}/default${accountQuery(resolvedAccountId)}`,
        {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({ accountId: resolvedAccountId }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed");
      await loadSecurityData();
      showSuccess("Default verification method updated");
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteEmail = async (methodId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/account/verification-methods/${methodId}${accountQuery(resolvedAccountId)}`,
        {
          method: "DELETE",
          headers: authHeaders(token),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed");
      await loadSecurityData();
      showSuccess("Verification method removed");
    } catch (err) {
      showError(err.message);
    }
  };

  const statusLabel = isEnabled
    ? "Enabled"
    : hasEmailMethod && !forceOn
      ? "Disabled"
      : hasEmailMethod
        ? "Disabled"
        : "No verification methods are enabled";

  const inlineRow = compact ? (
    <div className="pt-2 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <p className="font-medium text-gray-800">2-Step Verification</p>
        <button
          type="button"
          onClick={() => setTwoStepOpen(true)}
          className="text-xs font-semibold text-buttonRed hover:underline"
        >
          Manage
        </button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-xs text-gray-500">Status: {statusLabel}</p>
        <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            className="accent-[#DC1F00]"
            checked={forceOn}
            disabled={loading}
            onChange={(e) => handleToggle2FA(e.target.checked)}
          />
          Force 2FA
        </label>
      </div>
    </div>
  ) : (
    <div className="mt-4 mb-8 bg-transparent border-0 shadow-none p-0 rounded-none">
      <h2 className="text-lg font-semibold mb-4">2-Step Verification</h2>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[#00000080]">Status:</span>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              isEnabled
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {isEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[#00000080]">Force 2FA :</span>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#DC1F00]"
              checked={forceOn}
              disabled={loading}
              onChange={(e) => handleToggle2FA(e.target.checked)}
            />
            <span>{forceOn ? "Enabled" : "Enable"}</span>
          </label>
        </div>

        <div className="pt-2">
          <button
            type="button"
            onClick={() => setTwoStepOpen(true)}
            className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white text-sm font-medium rounded-md transition-colors"
          >
            Manage
          </button>
        </div>
      </div>
      {!isEnabled && (
        <p className="text-sm text-gray-500">{statusLabel}</p>
      )}
    </div>
  );

  return (
    <>
      {inlineRow}

      {twoStepOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#f5f5f5] w-[550px] max-w-[95vw] rounded-md shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b bg-white">
              <h2 className="text-lg font-semibold text-gray-800">
                2-Step Verification
              </h2>
            </div>
            <div className="p-6 space-y-5 bg-white">
              <div className="flex justify-between items-center opacity-60">
                <div>
                  <p className="font-medium">Text Message</p>
                  <p className="text-sm text-gray-500">
                    Verification codes are sent by text message
                  </p>
                </div>
                <span className="px-3 py-1 text-xs rounded bg-gray-100 text-gray-600">
                  Coming soon
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-sm text-gray-500">
                    Verification codes are sent by email
                  </p>
                </div>
                {hasEmailMethod ? (
                  <button
                    type="button"
                    onClick={() => openPasswordGate("manageEmail")}
                    className="px-4 py-1 border border-buttonRed text-buttonRed text-sm rounded"
                  >
                    MANAGE
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openPasswordGate("addEmail")}
                    className="px-4 py-1 bg-red-600 text-white text-sm rounded"
                  >
                    TURN ON
                  </button>
                )}
              </div>

              <div className="flex justify-between items-center opacity-60">
                <div>
                  <p className="font-medium">Authenticator App</p>
                  <p className="text-sm text-gray-500">
                    Verification code is generated by an app such as Authy or
                    Google Authenticator
                  </p>
                </div>
                <span className="px-3 py-1 text-xs rounded bg-gray-100 text-gray-600">
                  Coming soon
                </span>
              </div>
            </div>
            <div className="flex justify-end px-6 py-3 bg-white border-t">
              <button
                type="button"
                onClick={() => setTwoStepOpen(false)}
                className="text-sm text-red-600 font-semibold"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {verifyOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] max-w-[95vw] rounded shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Verify Your Account</h2>
            <p className="text-sm text-gray-500 mb-5">
              To continue, first verify it&apos;s you
            </p>
            {userDisplayName ? (
              <div className="mb-2 text-center font-medium">{userDisplayName}</div>
            ) : null}
            <div className="relative mb-2">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password*"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border px-3 py-2 pr-10"
              />
              <span
                role="button"
                tabIndex={0}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {passwordError && (
              <p className="text-red-500 text-sm mb-2">{passwordError}</p>
            )}
            <div className="flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={() => {
                  setVerifyOpen(false);
                  setPassword("");
                  setPendingAction(null);
                }}
                className="text-gray-600"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={verifyPasswordAndContinue}
                className="text-red-600 font-semibold"
              >
                NEXT
              </button>
            </div>
          </div>
        </div>
      )}

      {emailSetupOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] max-w-[95vw] rounded shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Email Setup</h2>
            <p className="text-sm text-gray-500 mb-4">
              The app will only use this email for verifying your account
              security.
            </p>
            <label className="block text-sm text-gray-600 mb-1">Email*</label>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              className="w-full border px-3 py-2 mb-4"
            />
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={emailMakeDefault}
                onChange={(e) => setEmailMakeDefault(e.target.checked)}
              />
              Make this the default 2-step verification method
            </label>
            {errorMsg && <p className="text-sm text-red-600 mb-2">{errorMsg}</p>}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => setEmailSetupOpen(false)}
                className="text-gray-600"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={sendEmailOtp}
                disabled={loading || !emailAddress}
                className="text-red-600 font-semibold"
              >
                NEXT
              </button>
            </div>
          </div>
        </div>
      )}

      {emailOtpOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[480px] max-w-[95vw] rounded shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Verify Email</h2>
            <p className="text-sm text-gray-500 mb-4">
              Enter the 6-digit code sent to your email
            </p>
            <input
              type="text"
              maxLength={6}
              value={emailOtp}
              onChange={(e) => setEmailOtp(e.target.value)}
              className="w-full border px-3 py-2 mb-4 text-center tracking-widest"
              placeholder="123456"
            />
            {errorMsg && <p className="text-sm text-red-600 mb-2">{errorMsg}</p>}
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={resendEmailOtp}
                disabled={loading}
                className="text-sm text-gray-600"
              >
                Resend
              </button>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setEmailOtpOpen(false)}
                  className="text-gray-600"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={verifyEmailOtp}
                  disabled={loading}
                  className="text-red-600 font-semibold"
                >
                  VERIFY
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {emailListOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[520px] max-w-[95vw] rounded shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Emails</h2>
            </div>
            <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {emailMethods.length === 0 ? (
                <p className="text-sm text-gray-500">No email methods</p>
              ) : (
                emailMethods.map((m) => (
                  <div
                    key={m.verification_method_id}
                    className="flex items-center justify-between border-b pb-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{m.email}</span>
                      {m.is_default || m.default ? (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          Default
                        </span>
                      ) : null}
                    </div>
                    <ActionDropdown
                      actions={[
                        ...(!(m.is_default || m.default)
                          ? [
                              {
                                label: "Set as Default",
                                onClick: () =>
                                  setDefaultEmail(m.verification_method_id),
                              },
                            ]
                          : []),
                        {
                          label: "Delete",
                          onClick: () => deleteEmail(m.verification_method_id),
                          danger: true,
                        },
                      ]}
                    />
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-between px-6 py-3 border-t">
              <button
                type="button"
                onClick={() => {
                  setEmailListOpen(false);
                  setEmailSetupOpen(true);
                }}
                className="text-sm text-gray-600 font-semibold"
              >
                ADD EMAIL
              </button>
              <button
                type="button"
                onClick={() => setEmailListOpen(false)}
                className="text-sm text-red-600 font-semibold"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
